// Single source of truth for site copy & structure.
// Update once, reflects everywhere.

export const NAV_LINKS = [
  { to: "/",        label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/work",    label: "Work" },
  { to: "/about",   label: "About" },
  { to: "/contact", label: "Contact" },
];

export const SERVICES = [
  {
    slug: "ai",
    title: "AI Solutions & Automation",
    short: "Reasoning agents, RAG over your data, and pipelines that take real work off your team's plate.",
    long: "We build production AI — not demos. Reasoning agents wired into your real systems, retrieval over your real data, and evaluations that catch regressions before customers do. Model-agnostic across Claude, GPT, Gemini, and open weights — chosen per workload.",
    tags: ["RAG pipelines", "Multi-agent workflows", "Evals & observability", "Fine-tuning", "Voice & vision"],
    icon: "ai",
  },
  {
    slug: "web",
    title: "Web Application Development",
    short: "Full-stack web apps with thoughtful UX, real performance budgets, and durable foundations.",
    long: "Typed end-to-end, observable in production, accessible by default. We treat performance as a feature and infrastructure as design — Next.js, tRPC, Postgres, Redis, edge rendering, and OpenTelemetry from day one.",
    tags: ["Next.js · Remix", "TypeScript · tRPC", "Postgres · Redis", "Auth · billing · admin", "Edge & RSC"],
    icon: "web",
  },
  {
    slug: "mobile",
    title: "Mobile App Development",
    short: "Native-feeling iOS & Android products built with React Native, Swift or Kotlin.",
    long: "React Native for speed, Swift / Kotlin where it matters. Tight design loops, real-device QA, App Store craft. We ship offline-first apps with crash analytics, push, and deep links wired up before launch.",
    tags: ["React Native · Expo", "Swift · Kotlin", "Push & deep links", "Offline-first", "Crash analytics"],
    icon: "phone",
  },
  {
    slug: "software",
    title: "Custom Software Development",
    short: "Internal tools, platforms, and SaaS engineered for the way your business actually runs.",
    long: "From admin consoles to vertical SaaS, we build the systems your team actually wants to open in the morning. Multi-tenant architecture, workflow engines, role-based access, and reporting that earns its place in the all-hands.",
    tags: ["Multi-tenant SaaS", "Workflow engines", "Reporting · BI", "API platforms", "On-prem / hybrid"],
    icon: "code",
  },
  {
    slug: "automation",
    title: "Business Process Automation",
    short: "Workflow engines, integrations, and bots that turn manual ops into compounding leverage.",
    long: "We map the work your team actually does, then automate the parts that don't need a human. Slack bots, document pipelines, CRM syncs, billing automations — all observable, idempotent, and easy to roll back.",
    tags: ["Workflow orchestration", "Zapier · n8n · custom", "ETL pipelines", "Slack · email bots", "CRM integrations"],
    icon: "flow",
  },
  {
    slug: "consulting",
    title: "Product Strategy & Consulting",
    short: "Pre-build clarity — discovery, roadmaps, technical due diligence, and architecture review.",
    long: "Before you commit to building, get a senior team to pressure-test the idea, the architecture, and the economics. Output: a sharp brief and a buildable roadmap, whether you build with us or anyone else.",
    tags: ["Product discovery", "Technical audits", "Architecture review", "Team coaching", "Fractional CTO"],
    icon: "compass",
  },
];

export const ENGAGEMENTS = [
  {
    name: "Sprint",
    label: "01 · Validate",
    price: "$24k",
    unit: "4 weeks · fixed scope",
    desc: "Go from idea to working prototype in a month. Ideal for validating a concept, pitching investors, or de-risking a build.",
    features: [
      "Discovery + design sprint",
      "Working prototype in production",
      "Architecture & roadmap doc",
      "1 senior engineer + designer",
      "Weekly demos",
    ],
    featured: false,
  },
  {
    name: "Build",
    label: "02 · Ship",
    price: "$80k+",
    unit: "8–14 weeks · v1 launch",
    desc: "End-to-end product build with a small senior team. The most common engagement — kickoff to first paying customer.",
    features: [
      "Discovery, design, engineering",
      "AI integration if applicable",
      "QA, accessibility, observability",
      "2–3 senior engineers + designer",
      "Launch readiness & handoff",
    ],
    featured: true,
  },
  {
    name: "Partner",
    label: "03 · Scale",
    price: "Retainer",
    unit: "Ongoing · monthly",
    desc: "Embedded team that scales with you. Performance, evals, new features, on-call coverage — everything past launch.",
    features: [
      "Embedded senior team",
      "SRE & on-call coverage",
      "Continuous deployment",
      "Quarterly roadmap reviews",
      "Direct Slack channel",
    ],
    featured: false,
  },
];

export const PROCESS_STEPS = [
  { num: "01", title: "Discover", body: "We pressure-test the idea against real users, real economics, and real constraints. Output: a sharp brief everyone aligns to.", list: ["Stakeholder interviews", "User research", "Technical audit", "Success metrics"] },
  { num: "02", title: "Design",   body: "Information architecture, flows, and high-fidelity UI. We design in the browser early so it never feels like Figma fiction.", list: ["IA & flows", "Hi-fi UI in code", "Design system", "Prototype testing"] },
  { num: "03", title: "Build",    body: "Production engineering with weekly demos. Test coverage, CI, observability, and a deploy pipeline from day one.",                list: ["Frontend & backend", "AI integration", "QA & accessibility", "Launch readiness"] },
  { num: "04", title: "Scale",    body: "Performance tuning, instrumentation, and a clean handoff or ongoing partnership. Your team owns it; we're a phone call away.",  list: ["Performance & SRE", "Analytics & evals", "Documentation", "Ongoing support"] },
];

export const REASONS = [
  { num: "01", title: "Senior team, no juniors learning on your dime.", body: "Every engagement is led by a principal-level designer and engineer. We hire for taste, judgement, and ownership." },
  { num: "02", title: "AI fluency, not AI hype.",                       body: "We've shipped agents, RAG, and evals in production. We know which problems AI actually solves — and which it doesn't." },
  { num: "03", title: "Design and engineering under one roof.",         body: "No throw-it-over-the-wall. Designers ship Figma and code, engineers care about pixels and copy. One team, one brief." },
  { num: "04", title: "Built for handoff, not lock-in.",                body: "Clean code, documented architecture, your repos and your cloud. We make ourselves replaceable on day one — and earn the second engagement." },
];

export const OUTCOMES = [
  { num: "3.4×",  label: "Median revenue lift, year one", body: "Across 18 founder-led engagements, clients who launched a Voila Tech-built product tripled topline within 12 months — half came from product, half from the operating leverage automation unlocks.", large: true },
  { num: "62%",   label: "Manual ops eliminated",         body: "Average reduction in repetitive workflows after a Voila automation engagement." },
  { num: "12 wk", label: "Idea to first ship",            body: "Median time from kickoff to v1 in production for a custom AI-powered application." },
  { num: "$0",    label: "Vendor lock-in",                body: "You own the repo, the infrastructure, and the IP. Always. Documented for handoff." },
  { num: "98%",   label: "Client retention",              body: "Most engagements lead to a second one. We ship outcomes, not deliverables." },
];

export const TRUSTED_LOGOS = [
  { name: "Northwind",  variant: "default" },
  { name: "Aperture",   variant: "serif" },
  { name: "FOLD/CO",    variant: "mono" },
  { name: "Lumen Labs", variant: "default" },
  { name: "Sundial",    variant: "serif" },
  { name: "HEX·9",      variant: "mono" },
];

export const CASE_STUDIES = [
  {
    slug: "northwind-agent",
    glyph: "N",
    client: "Northwind",
    industry: "Logistics",
    year: "2025",
    title: "A multi-agent ops desk that resolves 73% of tickets autonomously.",
    desc: "Replaced a 14-person triage team with a multi-agent system that classifies, retrieves, drafts, and ships responses — with human-in-the-loop on edge cases. Built on Claude with custom evals.",
    tags: ["Multi-agent", "Claude", "Postgres", "OpenTelemetry"],
    featured: true,
  },
  {
    slug: "aperture-platform",
    glyph: "A",
    client: "Aperture",
    industry: "Creative SaaS",
    year: "2025",
    title: "A creator platform that grew to 40k MAU in 90 days.",
    desc: "Full-stack web app with real-time collaboration, asset pipelines, and a billing system that scaled to $1.4M ARR in the first quarter. Next.js, Postgres, S3, Stripe.",
    tags: ["Next.js", "Real-time", "Stripe", "S3"],
    featured: false,
  },
  {
    slug: "fold-mobile",
    glyph: "F",
    client: "FOLD/CO",
    industry: "FinTech",
    year: "2024",
    title: "A finance app that hit App Store Editor's Choice in week six.",
    desc: "React Native app with native bridges for biometrics and Apple Pay. Offline-first ledger, encrypted local storage, and a design system shared with the marketing site.",
    tags: ["React Native", "Swift bridge", "Plaid", "Sentry"],
    featured: false,
  },
  {
    slug: "lumen-rag",
    glyph: "L",
    client: "Lumen Labs",
    industry: "BioTech R&D",
    year: "2024",
    title: "RAG over 2.4M research papers with sub-second retrieval.",
    desc: "Hybrid retrieval (BM25 + dense vectors) tuned per query type, with eval harness running on every prompt change. Cut researcher time-to-answer from 18 minutes to 22 seconds.",
    tags: ["RAG", "pgvector", "Evals", "Python"],
    featured: false,
  },
  {
    slug: "sundial-automation",
    glyph: "S",
    client: "Sundial",
    industry: "DTC Beauty",
    year: "2024",
    title: "Automating a 9-step fulfillment workflow into one.",
    desc: "Replaced a Zapier graveyard with a typed workflow engine — observable, retryable, idempotent. Saved 22 hours/week and unblocked international expansion.",
    tags: ["Workflow", "TypeScript", "Temporal", "Slack"],
    featured: false,
  },
  {
    slug: "hex9-internal",
    glyph: "H",
    client: "HEX·9",
    industry: "Industrial IoT",
    year: "2024",
    title: "An internal tool the ops team voluntarily opens at 8am.",
    desc: "Multi-tenant admin console replacing a tangle of Airtable bases and Retool screens. Role-based access, audit logs, and a CSV export everyone secretly loves.",
    tags: ["React", "Postgres", "RBAC", "Audit logs"],
    featured: false,
  },
];

export const TEAM = [
  { initials: "JR", name: "Jaya Rayankula",      role: "Founder",                     bio: "PhD-holding founder who set the bar for Voila Tech's research-first approach. Bridges deep academic rigor with what actually ships to production." },
  { initials: "CM", name: "Chava Rama Murthy",   role: "Founder",                     bio: "Businessman and founder who keeps the studio commercially sharp. Two decades of operating experience translating customer problems into honest engagements." },
  { initials: "GK", name: "Gokul",               role: "Co-Founder & Engineering · AI Architect · Product",  bio: "Ten years building production systems across AI, fintech, and SaaS. Wears three hats — AI architect, engineering lead, and product manager — and keeps them from colliding." },
  { initials: "TR", name: "Taraka Rama",         role: "Co-Founder & Backend Engineering", bio: "Builds the systems that don't go down. Owns API design, data, and the deep infrastructure work that makes the rest of the product feel effortless." },
  { initials: "RS", name: "Ramya Sree",          role: "People Solutions",            bio: "Owns hiring, talent, and the human side of the studio. The reason senior engineers stay, and the reason new ones land well." },
  { initials: "AR", name: "Akhil Rayankula",     role: "Head of Data & Analytics",    bio: "Turns raw operational data into decisions clients can act on. Owns the data stack — warehouse, models, dashboards — and the questions that should be asked of it." },
  { initials: "SP", name: "Shweta Pawar",        role: "Head of Marketing",           bio: "Runs brand, positioning, and the story we tell. Background in B2B marketing for technical products that refuse to sound like everyone else." },
];

export const VALUES = [
  { title: "Senior craft, end-to-end.",   desc: "No juniors learning on your dime. Every line of code, every pixel — owned by someone who's shipped it before." },
  { title: "Honest about AI.",            desc: "We've shipped enough production AI to know what it's good at and where it falls over. We'll tell you when not to use it." },
  { title: "Default transparent.",        desc: "Weekly demos, public roadmaps, candid Slack channels. If something's slipping, you hear it from us first." },
  { title: "Built for handoff.",          desc: "We work to make ourselves replaceable from day one. Documentation, repos, and infra you actually own." },
];

export const COMPANY = {
  name: "Voila Tech",
  tagline: "Premium AI-powered software for modern businesses.",
  email: "thevoilatech@gmail.com",
  phones: ["+91 6305 667 280", "+91 9791 901 104"],
  locations: ["Bengaluru", "Chennai"],
  social: {
    linkedin: "https://www.linkedin.com/company/the-voila-tech/",
    github:   "",
    twitter:  "",
  },
  status: "Now booking · Q3 2026",
};
